import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as styles from './adminNavigation.css';
import { MdSchool, MdDashboard, MdWork, MdArticle, MdLogout, MdHome, MdPerson, MdCode, MdWorkHistory, MdSettings, MdBolt, MdAccountCircle, MdSportsEsports, MdFormatQuote, MdMenu, MdClose } from 'react-icons/md';
// ✅ CORRIGÉ - Import avec le bon chemin
import { portfolioApi } from '../../../shared/services/portfolioApi';

const AdminNavigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileOpen, setIsMobileOpen] = React.useState(false);

  // Fermer le menu mobile à chaque changement de route
  React.useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      // Déconnexion via l'API
      await portfolioApi.auth.logout();
      
      // Redirection vers la page de connexion
      navigate('/admin/login', { replace: true });
      
      } catch (error) {
      // Forcer la déconnexion même en cas d'erreur
      portfolioApi.clearAuth();
      navigate('/admin/login', { replace: true });
    }
  };

  const menuItems = [
    {
      path: '/admin/dashboard',
      label: 'Dashboard',
      icon: MdDashboard,
      description: 'Vue d\'ensemble'
    },
    {
      path: '/admin/settings',
      label: 'Paramètres',
      icon: MdSettings,
      description: 'Feature flags et options'
    },
    {
      path: '/admin/about',
      label: 'À propos',
      icon: MdPerson,
      description: 'Informations personnelles'
    },
    {
      path: '/admin/projects',
      label: 'Projets',
      icon: MdWork,
      description: 'Gérer les projets'
    },
    {
      path: '/admin/services',
      label: 'Services',
      icon: MdWork,
      description: 'Gérer les services'
    },
    {
      path: '/admin/skills', 
      label: 'Compétences',
      icon: MdCode,
      description: 'Gérer les compétences'
    },
    {
      path: '/admin/education',  
      label: 'Formation',
      icon: MdSchool,
      description: 'Gérer les formations'
    },
    {
      path: '/admin/experiences',  
      label: 'Expériences',
      icon: MdWorkHistory,
      description: 'Gérer les expériences'
    },
    {
      path: '/admin/hobbies',  
      label: 'Loisirs',
      icon: MdSportsEsports,
      description: 'Gérer les loisirs'
    },
    {
      path: '/admin/life-quotes',
      label: 'Citations',
      icon: MdFormatQuote,
      description: 'Gérer les citations'
    },
    {
      path: '/admin/sections',
      label: 'Sections',
      icon: MdArticle,
      description: 'Contenu du site'
    }
  ];

  // ✅ CORRIGÉ - Récupération des informations utilisateur
  const [userInfo, setUserInfo] = React.useState({
    email: 'admin@portfolio.com',
    name: 'Administrateur'
  });

  React.useEffect(() => {
    const loadUserInfo = async () => {
      try {
        // Essayer de récupérer l'utilisateur depuis l'API
        const userResponse = await portfolioApi.auth.getCurrentUser();
        if (userResponse.success && userResponse.data) {
          const fallbackName = userResponse.data.first_name || (userResponse.data.email ? userResponse.data.email.split('@')[0] : undefined) || 'Administrateur';
          setUserInfo({
            email: userResponse.data.email || 'admin@portfolio.com',
            name: fallbackName
          });
        } else {
          // Fallback : récupérer depuis le localStorage
          const cachedUser = localStorage.getItem('portfolio_user');
          if (cachedUser) {
            const user = JSON.parse(cachedUser);
            const fallbackName = user.first_name || (user.email ? user.email.split('@')[0] : undefined) || 'Administrateur';
            setUserInfo({
              email: user.email || 'admin@portfolio.com',
              name: fallbackName
            });
          }
        }
      } catch (error) {
        // Garder les valeurs par défaut
      }
    };

    loadUserInfo();
  }, []);

  return (
    <>
      <button
        type="button"
        className={styles.mobileToggle}
        aria-label={isMobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        aria-expanded={isMobileOpen}
        onClick={() => setIsMobileOpen((v) => !v)}
      >
        {isMobileOpen ? <MdClose /> : <MdMenu />}
      </button>

      {isMobileOpen && (
        <div
          className={styles.backdrop}
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <nav className={`${styles.adminNav} ${isMobileOpen ? styles.navOpen : ''}`}>
      <div className={styles.navHeader}>
        <div className={styles.logo}>
          <span className={styles.logoIcon}><MdBolt /></span>
          <h2 className={styles.navTitle}>Admin Panel</h2>
        </div>
        <div className={styles.userInfo}>
          <div className={styles.userAvatar}><MdAccountCircle /></div>
          <div className={styles.userDetails}>
            <span className={styles.userEmail}>{userInfo.email}</span>
            <span className={styles.userRole}>{userInfo.name}</span>
          </div>
        </div>
      </div>

      <div className={styles.navBody}>
        <div className={styles.navSection}>
          <h3 className={styles.navSectionTitle}>Navigation principale</h3>
          <ul className={styles.navList}>
            {menuItems.map(item => {
              const IconComponent = item.icon;
              const isActive = location.pathname === item.path || 
                (item.path === '/admin/dashboard' && location.pathname === '/admin') ||
                (item.path === '/admin/life-quotes' && location.pathname.includes('/admin/life-quotes'));
              
              return (
                <li key={item.path}>
                  <Link 
                    to={item.path}
                    className={`${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
                    title={item.description}
                  >
                    <div className={styles.navItemIcon}>
                      <IconComponent />
                    </div>
                    <div className={styles.navItemContent}>
                      <span className={styles.navItemLabel}>{item.label}</span>
                      <span className={styles.navItemDescription}>{item.description}</span>
                    </div>
                    {isActive && <div className={styles.activeIndicator}></div>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        <div className={styles.navSection}>
          <h3 className={styles.navSectionTitle}>Actions rapides</h3>
          <div className={styles.quickActions}>
            <Link to="/admin/about" className={styles.quickAction}>
              <MdPerson className={styles.quickActionIcon} />
              <span>Éditer profil</span>
            </Link>
            <Link to="/admin/projects" className={styles.quickAction}>
              <MdWork className={styles.quickActionIcon} />
              <span>Nouveau projet</span>
            </Link>
            <Link to="/admin/hobbies" className={styles.quickAction}>
              <MdSportsEsports className={styles.quickActionIcon} />
              <span>Ajouter loisir</span>
            </Link>
            <Link to="/admin/life-quotes" className={styles.quickAction}>
              <MdFormatQuote className={styles.quickActionIcon} />
              <span>Nouvelle citation</span>
            </Link>
          </div>
        </div>
      </div>

      <div className={styles.navFooter}>
        <Link to="/" className={styles.navItem} target="_blank" rel="noopener noreferrer">
          <div className={styles.navItemIcon}>
            <MdHome />
          </div>
          <div className={styles.navItemContent}>
            <span className={styles.navItemLabel}>Voir le site</span>
            <span className={styles.navItemDescription}>Ouvrir dans un nouvel onglet</span>
          </div>
        </Link>
        
        <button onClick={handleLogout} className={styles.logoutButton}>
          <div className={styles.navItemIcon}>
            <MdLogout />
          </div>
          <div className={styles.navItemContent}>
            <span className={styles.navItemLabel}>Déconnexion</span>
            <span className={styles.navItemDescription}>Se déconnecter</span>
          </div>
        </button>

        <div className={styles.navFooterInfo}>
          <p className={styles.footerText}>Portfolio Admin v1.0</p>
          <p className={styles.footerText}>Jean-François CHAUSSOY</p>
        </div>
      </div>
    </nav>
    </>
  );
};

export { AdminNavigation };
export default AdminNavigation;